import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { requestPasswordReset, resetPasswordByCode } from '../../utilities/api'
import { isMatching } from '../../utilities/helpers'

import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import { PasswordInput } from '../PasswordInput'
import { Error } from '../Error'
import { SuccessContainer } from '../SuccessContainer'

export default function ForgotPasswordWrapper() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const isResetStep = Boolean(code)
  const inputEl = useRef()

  const [credentials, setCredentials] = useState({
    identifier: '',
    password: '',
  })

  const [isValid, setIsValid] = useState(true)
  const [errorType, setErrorType] = useState('')
  const [isEmailRequestSuccess, setIsEmailRequestSuccess] = useState(false)
  const [isResetSuccess, setIsResetSuccess] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!isValid) {
      setIsValid(true)
      setErrorType('')
    }
  }, [credentials.identifier, credentials.password])

  const handleCredentials = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value })
  }

  const handleRequestReset = async () => {
    await requestPasswordReset(credentials.identifier)
    setIsEmailRequestSuccess(true)
  }

  const handleResetPassword = async () => {
    if (!isMatching(credentials.password, inputEl.current.value)) {
      setIsValid(false)
      setErrorType('match')
      return
    }

    await resetPasswordByCode({
      code,
      password: credentials.password,
      passwordConfirmation: inputEl.current.value,
    })
    setIsResetSuccess(true)
    setTimeout(() => {
      navigate('/login')
    }, 2000)
  }

  const submit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    setIsValid(true)
    setErrorType('')
    try {
      if (isResetStep) {
        await handleResetPassword()
      } else {
        await handleRequestReset()
      }
    } catch (err) {
      console.error(err)
      setIsValid(false)
      setErrorType('')
    } finally {
      setIsSaving(false)
    }
  }

  if (isResetSuccess) {
    return (
      <section className="wrapper-xs">
        <SuccessContainer>
          <h1 className="heading">Password reset successfully!</h1>
          <p className="text-muted">Redirecting you to login...</p>
        </SuccessContainer>
      </section>
    )
  }

  return (
    <section className="wrapper-xs">
      <header className="mb-5">
        <h1 className="fs-md">Password reset</h1>
        <h2 className="text-muted normal fw-normal">
          {isResetStep
            ? 'Enter a new, secure password that you will use to log in to your account.'
            : 'Please enter the email address associated with your account and we will send you a link to reset your password.'}
        </h2>
      </header>
      <Form onSubmit={submit}>
        {!isResetStep && (
          <Form.Group className="form-group" controlId="identifier">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="identifier"
              placeholder="e.g mail@example.com"
              value={credentials.identifier}
              onChange={handleCredentials}
              required
              className={`${!isValid ? 'error-border' : ''}`}
            />
          </Form.Group>
        )}
        {isResetStep && (
          <>
            <Form.Group className="form-group" controlId="password">
              <Form.Label>Create a password</Form.Label>
              <PasswordInput
                value={credentials.password}
                isValid={isValid}
                handlePassword={handleCredentials}
              />
            </Form.Group>
            <Form.Group className="form-group" controlId="repeatPassword">
              <Form.Label>Repeat password</Form.Label>
              <PasswordInput
                placeholder="Confirm password"
                isValid={isValid}
                reference={inputEl}
              />
            </Form.Group>
          </>
        )}
        {!isValid && <Error type={errorType || undefined} />}
        {isEmailRequestSuccess && (
          <Alert variant="success" className="mt-3 mb-0">
            If an account exists for this email, check your inbox for a password
            reset link.
          </Alert>
        )}
        <Button
          type="submit"
          size="lg"
          className="mt-4 w-100 btn-next"
          disabled={
            isSaving ||
            (isResetStep
              ? credentials.password === ''
              : credentials.identifier === '')
          }
        >
          {isSaving
            ? isResetStep
              ? 'Resetting...'
              : 'Sending...'
            : isResetStep
              ? 'Reset password'
              : 'Send reset link'}
        </Button>
      </Form>
      <div className="text-center mt-4 caption">
        <span className="text-muted">
          Back to <Link to="/login">login</Link>
        </span>
      </div>
    </section>
  )
}
